import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import MuxVideoUpload from '../../song-detail-page/components/MuxVideoUpload';


const LEVELS = [
  { key: 'overview', label: 'Overview', color: 'blue' },
  { key: 'level_1', label: 'Level 1', color: 'green' },
  { key: 'level_2', label: 'Level 2', color: 'yellow' },
  { key: 'level_3', label: 'Level 3', color: 'orange' },
  { key: 'level_4', label: 'Level 4', color: 'red' },
  { key: 'level_5', label: 'Level 5', color: 'purple' },
];

const STATUS_CONFIG = {
  ready: { label: 'Ready', icon: 'CheckCircle', bg: 'bg-green-500 bg-opacity-20', text: 'text-green-300', border: 'border-green-500 border-opacity-40' },
  preparing: { label: 'Preparing', icon: 'Clock', bg: 'bg-yellow-500 bg-opacity-20', text: 'text-yellow-300', border: 'border-yellow-500 border-opacity-40' },
  waiting: { label: 'Waiting', icon: 'Hourglass', bg: 'bg-blue-500 bg-opacity-20', text: 'text-blue-300', border: 'border-blue-500 border-opacity-40' },
  errored: { label: 'Error', icon: 'AlertCircle', bg: 'bg-red-500 bg-opacity-20', text: 'text-red-300', border: 'border-red-500 border-opacity-40' },
  empty: { label: 'No Video', icon: 'VideoOff', bg: 'bg-white bg-opacity-5', text: 'text-blue-300 text-opacity-50', border: 'border-white border-opacity-10' },
};

const LEVEL_COLORS = {
  blue: { badge: 'bg-blue-500 bg-opacity-30 text-blue-200', ring: 'ring-blue-400' },
  green: { badge: 'bg-green-500 bg-opacity-30 text-green-200', ring: 'ring-green-400' },
  yellow: { badge: 'bg-yellow-500 bg-opacity-30 text-yellow-200', ring: 'ring-yellow-400' },
  orange: { badge: 'bg-orange-500 bg-opacity-30 text-orange-200', ring: 'ring-orange-400' },
  red: { badge: 'bg-red-500 bg-opacity-30 text-red-200', ring: 'ring-red-400' },
  purple: { badge: 'bg-purple-500 bg-opacity-30 text-purple-200', ring: 'ring-purple-400' },
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s)?.padStart(2, '0')}`;
};

const LevelSlot = ({ level, video, onTogglePublish, onView, toggling, onUpload }) => {
  const cfg = video ? (STATUS_CONFIG?.[video?.status] || STATUS_CONFIG?.waiting) : STATUS_CONFIG?.empty;
  const lc = LEVEL_COLORS?.[level?.color];
  const playbackId = video?.mux_playback_id;
  const duration = video?.duration_seconds ? formatDuration(video?.duration_seconds) : null;

  return (
    <div className={`rounded-lg border ${cfg?.border} ${cfg?.bg} p-3 flex flex-col gap-2 min-h-[110px]`}>
      {/* Level badge */}
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${lc?.badge}`}>
          {level?.label}
        </span>
        {video && (
          <button
            onClick={() => onTogglePublish(video)}
            disabled={toggling === video?.id}
            title={video?.published ? 'Unpublish' : 'Publish'}
            className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
              video?.published
                ? 'bg-green-500 bg-opacity-30 border-green-400 border-opacity-50 text-green-200 hover:bg-opacity-50' :'bg-white bg-opacity-10 border-white border-opacity-20 text-blue-200 hover:bg-opacity-20'
            } ${toggling === video?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            {toggling === video?.id ? '…' : video?.published ? 'Published' : 'Draft'}
          </button>
        )}
      </div>
      {/* Status */}
      <div className={`flex items-center gap-1.5 ${cfg?.text}`}>
        <Icon name={cfg?.icon} size={13} />
        <span className="text-xs font-medium">{cfg?.label}</span>
      </div>
      {/* Playback ID + duration */}
      {video ? (
        <div className="space-y-1">
          {playbackId ? (
            <div className="flex items-center gap-1">
              <Icon name="Link" size={11} className="text-blue-300 flex-shrink-0" />
              <span className="text-xs text-blue-200 font-mono truncate" title={playbackId}>
                {playbackId?.slice(0, 14)}…
              </span>
            </div>
          ) : (
            <span className="text-xs text-blue-300 text-opacity-50 italic">No playback ID</span>
          )}
          {duration && (
            <div className="flex items-center gap-1">
              <Icon name="Clock" size={11} className="text-blue-300" />
              <span className="text-xs text-blue-200">{duration}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-1">
            {playbackId && (
              <button
                onClick={() => onView(video)}
                className="text-xs text-blue-300 hover:text-white flex items-center gap-1 transition-colors"
              >
                <Icon name="Play" size={11} />
                Preview
              </button>
            )}
            <button
              onClick={onUpload}
              className="text-xs text-indigo-300 hover:text-white flex items-center gap-1 transition-colors"
              title="Replace video"
            >
              <Icon name="Upload" size={11} />
              Replace
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1 mt-auto">
          <span className="text-xs text-blue-300 text-opacity-40 italic">No video assigned</span>
          <button
            onClick={onUpload}
            className="text-xs text-indigo-300 hover:text-white flex items-center gap-1 transition-colors mt-1"
          >
            <Icon name="Upload" size={11} />
            Upload
          </button>
        </div>
      )}
    </div>
  );
};

const SongRow = ({ song, videosBySong, onTogglePublish, onView, toggling, isExpanded, onToggleExpand, onUploadForLevel, songTalentId }) => {
  const songVideos = videosBySong?.[song?.id] || {};
  const readyCount = LEVELS?.filter(l => songVideos?.[l?.key]?.status === 'ready')?.length;
  const completenessPercent = Math.round((readyCount / 6) * 100);

  const completenessColor =
    readyCount === 6 ? 'text-green-300' :
    readyCount >= 4 ? 'text-yellow-300' :
    readyCount >= 2 ? 'text-orange-300' : 'text-red-300';

  const barColor =
    readyCount === 6 ? 'bg-green-400' :
    readyCount >= 4 ? 'bg-yellow-400' :
    readyCount >= 2 ? 'bg-orange-400' : 'bg-red-400';

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden mb-4">
      {/* Song header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white hover:bg-opacity-5 transition-all text-left"
      >
        <div className="flex items-center gap-4 min-w-0">
          <Icon name="Music" size={18} className="text-blue-300 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-white truncate">{song?.title}</div>
            {song?.musicals?.title && (
              <div className="text-xs text-blue-200 truncate">{song?.musicals?.title}</div>
            )}
            {songTalentId && song?.talentName && (
              <div className="text-xs text-indigo-300 truncate flex items-center gap-1">
                <Icon name="User" size={10} />
                {song?.talentName}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-5 flex-shrink-0 ml-4">
          {/* Completeness indicator */}
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-white bg-opacity-10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${completenessPercent}%` }}
              />
            </div>
            <span className={`text-sm font-semibold ${completenessColor}`}>
              {readyCount}/6
            </span>
            <span className="text-xs text-blue-200">ready</span>
          </div>

          {/* Level dots */}
          <div className="hidden sm:flex items-center gap-1">
            {LEVELS?.map(l => {
              const v = songVideos?.[l?.key];
              const dotColor =
                v?.status === 'ready' ? 'bg-green-400' :
                v?.status === 'preparing' ? 'bg-yellow-400' :
                v?.status === 'waiting' ? 'bg-blue-400' :
                v?.status === 'errored' ? 'bg-red-400' : 'bg-white bg-opacity-20';
              return (
                <div
                  key={l?.key}
                  title={`${l?.label}: ${v ? v?.status : 'no video'}`}
                  className={`w-2.5 h-2.5 rounded-full ${dotColor}`}
                />
              );
            })}
          </div>

          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-blue-200" />
        </div>
      </button>
      {/* Level grid */}
      {isExpanded && (
        <div className="px-5 pb-5 pt-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {LEVELS?.map(level => (
              <LevelSlot
                key={level?.key}
                level={level}
                video={songVideos?.[level?.key] || null}
                onTogglePublish={onTogglePublish}
                onView={onView}
                toggling={toggling}
                onUpload={() => onUploadForLevel(song, level?.key, songTalentId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SongLevelGrid = ({ songs, videosBySong, onTogglePublish, onView, toggling, missingLevelFilter, onVideoUploaded, songTalentMap }) => {
  const [expandedSongs, setExpandedSongs] = useState({});
  const [uploadTarget, setUploadTarget] = useState(null); // { song, levelKey, talentId }

  const toggleExpand = (songId) => {
    setExpandedSongs(prev => ({ ...prev, [songId]: !prev?.[songId] }));
  };

  const expandAll = () => {
    const all = {};
    songs?.forEach(s => { all[s.id] = true; });
    setExpandedSongs(all);
  };

  const collapseAll = () => setExpandedSongs({});

  const handleUploadForLevel = (song, levelKey, talentId) => {
    setUploadTarget({ song, levelKey, talentId });
  };

  const handleUploadClose = () => {
    setUploadTarget(null);
  };

  const handleVideoReady = (video) => {
    setUploadTarget(null);
    onVideoUploaded?.();
  };

  // Filter songs by missing level if filter active
  const displaySongs = missingLevelFilter
    ? songs?.filter(song => {
        const sv = videosBySong?.[song?.id] || {};
        return !sv?.[missingLevelFilter] || sv?.[missingLevelFilter]?.status !== 'ready';
      })
    : songs;

  if (displaySongs?.length === 0) {
    return (
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
        <Icon name="CheckCircle" size={40} className="text-green-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">All songs have this level covered!</h3>
        <p className="text-blue-200 text-sm">No songs are missing a ready video for the selected level.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Expand/Collapse controls */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-blue-200">{displaySongs?.length} song{displaySongs?.length !== 1 ? 's' : ''}</span>
        <button onClick={expandAll} className="text-xs text-blue-300 hover:text-white underline transition-colors">Expand All</button>
        <button onClick={collapseAll} className="text-xs text-blue-300 hover:text-white underline transition-colors">Collapse All</button>
      </div>
      {displaySongs?.map(song => (
        <SongRow
          key={song?.id}
          song={song}
          videosBySong={videosBySong}
          onTogglePublish={onTogglePublish}
          onView={onView}
          toggling={toggling}
          isExpanded={!!expandedSongs?.[song?.id]}
          onToggleExpand={() => toggleExpand(song?.id)}
          onUploadForLevel={handleUploadForLevel}
          songTalentId={songTalentMap?.[song?.id] || null}
        />
      ))}

      {/* Inline MuxVideoUpload modal triggered from level slot */}
      {uploadTarget && (
        <MuxVideoUpload
          songId={uploadTarget?.song?.id}
          level={uploadTarget?.levelKey}
          talentId={uploadTarget?.talentId}
          onVideoReady={handleVideoReady}
          onClose={handleUploadClose}
        />
      )}
    </div>
  );
};

export { LEVELS };
export default SongLevelGrid;
